
  import { ActivityType, Lead, LeadActivity } from '@/entities/crm'
  import { Vue, Prop, Component, Watch } from 'vue-property-decorator'
  import dayjs from 'dayjs'
  import 'dayjs/locale/es'
  import utc from 'dayjs/plugin/utc'
  import timezone from 'dayjs/plugin/timezone'
  import localizedFormat from 'dayjs/plugin/localizedFormat'
  import { capitalize, fixDate } from '@/utils/general'
  import { mapGetters, mapActions } from 'vuex'
  import TaskDetail from '@/components/forms/fields/agenda/TaskDetail.vue'
  import { Query } from '@/entities/public/Resource/interfaces'
  import { formFilter } from '@/graphql/generated-types'

  // Configurar dayjs
  dayjs.extend(utc)
  dayjs.extend(timezone)
  dayjs.extend(localizedFormat)
  dayjs.locale('es')

  // Interface para eventos del calendario
  interface CalendarEvent {
    id: string
    name: string
    start: string | number
    end: string | number
    color: string
    timed: boolean
    details?: string
    readonly?: boolean // Indica si el evento es de solo lectura (no editable)
    hasConflict?: boolean // Indica si el evento tiene conflicto de horarios
    originalName?: string // Nombre original antes del conflicto
    originalColor?: string // Color original antes del conflicto
    icon?: string // Icono del evento
    startTime?: string
    endTime?: string
  }

@Component({
  components: {
    TaskDetail,
  },
  computed: {
      ...mapGetters('app', ['isMobile']),
    },
  methods: {
    ...mapActions('resources/form', ['fetchData']),
  },
  })
  export default class AgendaInspection extends Vue {
    @Prop({ type: String, default: '' }) currentSelectedTime!: string
    @Prop({ type: Boolean, default: false }) required!: boolean
    @Prop({ type: Boolean, default: false }) disabled!: boolean
    @Prop({ type: Array, default: () => [] }) fieldRequired!: string[]
    @Prop({ type: Array, default: () => [] }) agendas!: Array<LeadActivity>
    @Prop({ type: String, default: '' }) startHour!: string
    @Prop({ type: String, default: '' }) endHour!: string
    @Prop({ type: Boolean, default: false }) timeIsNotAvailable!: boolean
    @Prop({ type: String, default: '' }) selectedDate!: string
    @Prop({ type: Number, default: 30, validator: (value: number) => [5, 10, 15, 20, 30, 60, 120].includes(value) }) intervalMinutesProp!: number
    @Prop({ type: Number, default: 60, validator: (value: number) => value > 0 && value <= 120 }) taskDurationMinutes!: number
    @Prop({ type: Number, default: 1 }) maxConcurrentTasks!: number
    @Prop({ type: Object, default: null }) newAgenda!: {
    date: string;
    title: ActivityType;
    allowSelect: boolean;
    lead: Lead;
  };

      // Propiedades del store
    isMobile!: boolean
    fetchData!: (payload: {
      query: Query;
      filter?: formFilter;
      offset?: number;
      limit?: number;
      force?: boolean;
    }) => Promise<any>;

    // Propiedades del componente
    selectedTime = ''
    isDialogOpen = false
    value = ''
    events: CalendarEvent[] = []
    selectedOpen = false
    selectedElement: any = null
    selectedEvent: CalendarEvent = {
      id: '',
      name: '',
      start: '',
      end: '',
      color: '',
      timed: false,
      details: '',
    }

    temporarySelection = ''
    dragEvent: CalendarEvent | null = null
    dragTime: number | null = null
    extendOriginal: number | null = null
    createStart: number | null = null
    createEvent: CalendarEvent | null = null

    // Timer para actualizar la hora
    timeUpdateInterval: any = null

    // Propiedades para manejar conflictos de horarios
    hasConflict = false
    conflictTooltip = ''

    // Propiedades para el sistema de hover y detalles
    showDetailMenu = false
    positionX = 0
    positionY = 0
    leadDetail: { lead: Lead | null; task: LeadActivity | null } = {
      lead: null,
      task: null,
    }

    hoverTimer: any = null
    isLoadingDetail = false
    currentHoveredEvent: CalendarEvent | null = null

    // Colores disponibles para los eventos
    colors = [
      'blue',
      'indigo',
      'deep-purple',
      'cyan',
      'green',
      'orange',
      'grey darken-1',
    ]

    // Nombres de ejemplo para eventos
    names = [
      'Inspección disponible',
      'Cita programada',
      'Revisión técnica',
      'Evaluación',
    ]

    /**
     * Computed property para formatear la fecha seleccionada en español
     */
    get formattedSelectedDate (): string {
      if (this.selectedDate) {
        return capitalize(dayjs(fixDate(this.selectedDate)).format('dddd, D [de] MMMM [del] YYYY'))
      } else {
        return capitalize(dayjs().format('dddd, D [de] MMMM [del] YYYY'))
      }
    }

    /**
     * Computed property para mostrar la hora actual
     */
    get currentTime (): string {
      return dayjs().format('h:mm A')
    }

    /**
     * Computed property para la hora de inicio en formato string
     */
    get firstTime (): string {
      // Usar startHour como hora inicial del calendario
      return this.startHour || '00:00'
    }

    /**
     * Computed property para calcular cuántos intervalos mostrar
     */
    get intervalCount (): number {
      if (this.startHour && this.endHour) {
        // Parsing de hora de inicio
        const startParts = this.startHour.split(':')
        const startHours = parseInt(startParts[0]) || 8
        const startMinutes = parseInt(startParts[1]) || 0
        // Parsing de hora de fin
        const endParts = this.endHour.split(':')
                const endHours = parseInt(endParts[0]) || 19
        const endMinutes = parseInt(endParts[1]) || 0

        // Calcular el total de minutos en el rango
        const startTotalMinutes = startHours * 60 + startMinutes
        const endTotalMinutes = endHours * 60 + endMinutes
        const totalMinutes = endTotalMinutes - startTotalMinutes

        // Calcular cuántos intervalos caben en ese rango
        return Math.max(Math.ceil(totalMinutes / this.intervalMinutes), 1)
      }
      // Valor por defecto: desde las 00:00 hasta las 19:00
      const defaultMinutes = 19 * 60 // 19 horas en minutos
      return Math.ceil(defaultMinutes / this.intervalMinutes)
    }

    /**
     * Computed property para los minutos por intervalo
     */
    get intervalMinutes (): number {
      return this.intervalMinutesProp // Intervalos dinámicos basados en el prop
    }

    /**
     * Computed property para obtener la duración de la tarea en milisegundos
     */
    get taskDurationMs (): number {
      return this.taskDurationMinutes * 60 * 1000 // Convertir minutos a milisegundos
    }

        /**
     * Computed property para validar si hay conflicto de horarios
     */
    get hasScheduleConflict (): boolean {
      return this.hasConflict
    }

    /**
     * Computed property para deshabilitar el botón de confirmación
     */
    get isConfirmDisabled (): boolean {
      return !this.temporarySelection || this.hasScheduleConflict || (this.newAgenda && !this.newAgenda.allowSelect)
    }

    /**
     * Detecta si dos rangos de tiempo se solapan
     */
    hasTimeOverlap (start1: number, end1: number, start2: number, end2: number): boolean {
      return start1 < end2 && start2 < end1
    }

    /**
     * Cuenta cuántas tareas concurrentes hay en un rango de tiempo específico
     */
    countConcurrentTasks (startTime: number, endTime: number): number {
      let concurrentCount = 0

      // Contar eventos existentes (excluyendo el evento temporal actual)
      for (const event of this.events) {
        if (event === this.createEvent) continue // Excluir el evento que se está creando

        const eventStart = this.ensureTimeAsNumber(event.start)
        const eventEnd = this.ensureTimeAsNumber(event.end)

        if (this.hasTimeOverlap(startTime, endTime, eventStart, eventEnd)) {
          concurrentCount++
        }
      }

      return concurrentCount
    }

    /**
     * Resetea el estado de conflicto de todos los eventos
     */
    resetEventConflicts (): void {
      for (const event of this.events) {
        if (event.hasConflict) {
          if (event.originalName) event.name = event.originalName
          if (event.originalColor) event.color = event.originalColor
          event.hasConflict = false
        }
      }
    }

    /**
     * Cuenta conflictos para un evento específico
     */
    countConflictsForEvent (eventIndex: number): number {
      const currentEvent = this.events[eventIndex]
      const currentStart = this.ensureTimeAsNumber(currentEvent.start)
      const currentEnd = this.ensureTimeAsNumber(currentEvent.end)
      let conflictCount = 0

      for (let j = 0; j < this.events.length; j++) {
        if (eventIndex === j) continue
        const otherEvent = this.events[j]
        const otherStart = this.ensureTimeAsNumber(otherEvent.start)
        const otherEnd = this.ensureTimeAsNumber(otherEvent.end)

        if (this.hasTimeOverlap(currentStart, currentEnd, otherStart, otherEnd)) {
          conflictCount++
        }
      }
      return conflictCount
    }

    /**
     * Marca un evento como conflicto
     */
    markEventAsConflict (event: CalendarEvent): void {
      if (!event.originalName) event.originalName = event.name
      if (!event.originalColor) event.originalColor = event.color
      event.hasConflict = true
      event.name = 'Error'
      event.color = '#f44336'
      this.hasConflict = true
    }

    /**
     * Valida todos los eventos para detectar conflictos de horarios
     */
    validateAllEvents (): void {
      this.hasConflict = false
      this.conflictTooltip = `Tareas concurrentes máximas: ${this.maxConcurrentTasks}`

      this.resetEventConflicts()

      for (let i = 0; i < this.events.length; i++) {
        const currentEvent = this.events[i]
        if (currentEvent.readonly) continue

        const conflictCount = this.countConflictsForEvent(i)

        if (conflictCount >= this.maxConcurrentTasks) {
          this.markEventAsConflict(currentEvent)
        }
      }
    }

    /**
     * Valida y actualiza el estado de conflicto del evento temporal (mantener compatibilidad)
     */
    validateAndUpdateConflict (): void {
      this.validateAllEvents()
    }

    /**
     * Convierte una fecha del formato DD/MM/YYYY al formato YYYY-MM-DD
     */
    formatDateToISO (dateString: string): string {
      // Si ya está en formato ISO, devolverlo tal como está
      if (dateString.includes('-') && dateString.length === 10) {
        return dateString
      }

      // Si está en formato DD/MM/YYYY, convertirlo
      if (dateString.includes('/')) {
        const parts = dateString.split('/')
        if (parts.length === 3) {
          const day = parts[0].padStart(2, '0')
          const month = parts[1].padStart(2, '0')
          const year = parts[2]
          return `${year}-${month}-${day}`
        }
      }

      // Si no se puede convertir, intentar parsear con dayjs
      try {
        return dayjs(dateString).format('YYYY-MM-DD')
      } catch (error) {
        console.error('Error convirtiendo fecha:', dateString, error)
        return dayjs().format('YYYY-MM-DD')
      }
    }

    /**
     * Lifecycle hook - mounted
     */
    mounted (): void {
      // Actualizar la hora cada minuto y refrescar eventos si es necesario
      this.timeUpdateInterval = setInterval(() => {
        this.$forceUpdate()

        // Si el diálogo está abierto y es el día actual, refrescar eventos y estilos
        if (this.isDialogOpen && this.isSelectedDateToday()) {
          this.applyPastIntervalStyles()
        }
      }, 60000)
    }

    /**
     * Lifecycle hook - beforeDestroy
     */
    beforeDestroy (): void {
      if (this.timeUpdateInterval) {
        clearInterval(this.timeUpdateInterval)
      }

      // Limpiar timer de hover si existe
      if (this.hoverTimer) {
        clearTimeout(this.hoverTimer)
      }
    }

    /**
     * Formatea los intervalos del calendario
     */
    formatInterval (interval: any): string {
      const { hour, minute } = interval
      if (hour === 0 && minute === 0) return '12:00 AM'
      if (hour === 12 && minute === 0) return '12:00 PM'
      if (hour < 12) {
        return `${hour}:${minute.toString().padStart(2, '0')} AM`
      } else {
        const displayHour = hour === 12 ? 12 : hour - 12
        return `${displayHour}:${minute.toString().padStart(2, '0')} PM`
      }
    }

    /**
     * Abre el dialog de selección de horarios de manera segura
     */
    safeOpenDialog (): void {
      if (!this.isDialogOpen) {
        this.openTimeDialog()
      }
    }

    /**
     * Abre el dialog de selección de horarios
     */
    openTimeDialog (): void {
      if (this.disabled) return

      // No abrir si ya está abierto
      if (this.isDialogOpen) {
        return
      }

      this.isDialogOpen = true
      // Convertir selectedDate al formato ISO para el calendario
      const targetDate = this.selectedDate ? this.formatDateToISO(this.selectedDate) : dayjs().format('YYYY-MM-DD')
      this.value = targetDate

      // Refrescar el calendario y aplicar estilos correctos
      this.refreshCalendar()
    }

    /**
     * Cierra el dialog y limpia la selección temporal
     */
    closeDialog (): void {
      this.isDialogOpen = false
      this.temporarySelection = ''
      this.hasConflict = false
      this.conflictTooltip = ''

      // Limpiar estilos de intervalos pasados al cerrar el diálogo
      this.$nextTick(() => {
        const intervals = document.querySelectorAll('.v-calendar-daily__interval')
        intervals.forEach(interval => {
          interval.classList.remove('past-interval')
        })
      })
    }

    /**
     * Convierte el formato de tiempo de "h:mm A" a "HH:mm"
     */
    convertTimeFormat (timeString: string): string {
      try {
        // Parsear el tiempo en formato "h:mm A" y convertir a "HH:mm"
        return dayjs(timeString, 'h:mm A').format('HH:mm')
      } catch (error) {
        console.error('Error convirtiendo formato de tiempo:', timeString, error)
        return '00:00'
      }
    }

    /**
     * Extrae las horas de inicio y fin del temporarySelection
     */
    extractTimeRange (temporarySelection: string): { start: string; end: string } {
      try {
        // Dividir por " - " para obtener inicio y fin
        const [startTime, endTime] = temporarySelection.split(' - ')

        return {
          start: this.convertTimeFormat(startTime.trim()),
          end: this.convertTimeFormat(endTime.trim()),
        }
      } catch (error) {
        console.error('Error extrayendo rango de tiempo:', temporarySelection, error)
        return { start: '00:00', end: '00:00' }
      }
    }

    /**
     * Confirma la selección de horario y emite el evento
     */
    confirmSelection (): void {
      // Solo permitir confirmación si no hay conflictos
      if (!this.hasScheduleConflict && this.temporarySelection) {
        // Extraer las horas de inicio y fin del temporarySelection
        const timeRange = this.extractTimeRange(this.temporarySelection)

        // Emitir en el mismo formato que Agenda.vue
        this.$emit('time-range-selected', timeRange)

        // Actualizar el tiempo seleccionado para mostrar en el input
        this.selectedTime = this.temporarySelection

        // Cerrar el diálogo
        this.closeDialog()
      }
    }

    /**
     * Elimina un evento del calendario
     */
    deleteEvent (eventId: string): void {
      this.events = this.events.filter(event => event.id !== eventId)
      this.selectedOpen = false
    }

    /**
     * Actualiza un evento existente
     */
    updateEvent (): void {
      const index = this.events.findIndex(event => event.id === this.selectedEvent.id)
      if (index !== -1) {
        this.events.splice(index, 1, { ...this.selectedEvent })
      }
      this.selectedOpen = false
    }

    /**
     * Inicia el arrastre de un evento
     */
    startDrag ({ event, timed }: { event: CalendarEvent; timed: boolean }): void {
      if (event && timed && !event.readonly) {
        // Preservar información original si no existe
        if (!event.originalName) {
          event.originalName = event.name
        }
        if (!event.originalColor) {
          event.originalColor = event.color
        }

        this.dragEvent = event
        this.dragTime = null
        this.extendOriginal = null
      }
    }

    /**
     * Encuentra el primer evento editable (no readonly) en el calendario
     */
    findEditableEvent (): CalendarEvent | null {
      return this.events.find(event => !event.readonly) || null
    }

        /**
     * Actualiza el calendario con estrategia de reset
     */
    updateCalendarWithReset (): void {
      const eventsBackup = [...this.events]
      this.events = []

      this.$nextTick(() => {
        this.events = eventsBackup
        this.refreshCalendar()
      })
    }

    /**
     * Helper para refrescar el calendario de manera consistente
     */
    refreshCalendar (): void {
      // Múltiples estrategias para forzar actualización
      this.$forceUpdate()

      this.$nextTick(() => {
        if (this.$refs.calendar) {
          const calendar = this.$refs.calendar as any
          calendar.checkChange()

          // Intentar métodos adicionales si existen
          if (calendar.updateEvents) {
            calendar.updateEvents()
          }
          if (calendar.forceUpdate) {
            calendar.forceUpdate()
          }
        }

        // Segundo forceUpdate después del nextTick
        this.$forceUpdate()

        // Siempre aplicar estilos a intervalos (limpiará estilos para días futuros)
        this.applyPastIntervalStyles()
      })
    }

    /**
     * Aplica estilos CSS a los intervalos de tiempo que ya pasaron
     */
    applyPastIntervalStyles (): void {
      this.$nextTick(() => {
        // Buscar todos los intervalos del calendario
        const intervals = document.querySelectorAll('.v-calendar-daily__interval')

        // Si no es el día actual, remover todos los estilos de intervalos pasados
        if (!this.isSelectedDateToday() || !this.startHour || !this.endHour) {
          intervals.forEach(interval => {
            interval.classList.remove('past-interval')
          })
          return
        }

        const currentChileanTime = this.getCurrentChileanTime()
        const currentHour = currentChileanTime.hour()
        const currentMinute = currentChileanTime.minute()

        intervals.forEach((interval, index) => {
          // Calcular la hora del intervalo basado en el índice y configuración
          const startParts = this.startHour.split(':')
          const startHours = parseInt(startParts[0]) || 8
          const startMinutes = parseInt(startParts[1]) || 0

          const intervalTotalMinutes = startHours * 60 + startMinutes + (index * this.intervalMinutes)
          const intervalHour = Math.floor(intervalTotalMinutes / 60)
          const intervalMinute = intervalTotalMinutes % 60

          // Verificar si este intervalo ya pasó (con buffer de 5 minutos)
          const currentTotalMinutes = currentHour * 60 + currentMinute
          const intervalTime = intervalHour * 60 + intervalMinute

          if (intervalTime <= currentTotalMinutes + 5) {
            // Aplicar estilos de intervalo pasado solo si es hoy
            interval.classList.add('past-interval')
          } else {
            // Remover estilos si ya no aplican
            interval.classList.remove('past-interval')
          }
        })
      })
    }

    /**
     * Actualiza un evento existente con nueva posición de manera reactiva
     */
    updateEventPosition (event: CalendarEvent, startTime: number): void {
      // Crear un nuevo objeto evento completamente nuevo
      const updatedEvent = {
        ...event,
        start: startTime,
        end: startTime + this.taskDurationMs,
      }

      // Reemplazar el evento en el array de manera completamente reactiva
      const eventIndex = this.events.findIndex(e => e.id === event.id)
      if (eventIndex !== -1) {
        this.events = [
          ...this.events.slice(0, eventIndex),
          updatedEvent,
          ...this.events.slice(eventIndex + 1),
        ]
      }
    }

    /**
     * Inicia la creación de un nuevo evento o mueve el evento editable existente
     */
    startTime (tms: any): void {
      const mouse = this.toTime(tms)

      if (!this.isTimeInAllowedRange(tms)) {
        return
      }

      if (this.dragEvent && this.dragTime === null) {
        const start = this.dragEvent.start as number
        this.dragTime = mouse - start
      } else {
        this.handleTimeClick(tms, mouse)
      }
    }

    /**
     * Maneja el click en tiempo para mover o crear eventos
     */
    handleTimeClick (tms: any, mouse: number): void {
      const newStartTime = this.roundTime(mouse)
      const newEndTime = newStartTime + this.taskDurationMs

      // Verificar si el slot está disponible (no ha pasado si es hoy)
      if (!this.isTimeRangeAvailable(newStartTime, newEndTime)) {
        // No permitir selección de slots pasados
        return
      }

      this.temporarySelection = `${this.formatEventTime(newStartTime)} - ${this.formatEventTime(newEndTime)}`

      const editableEvent = this.findEditableEvent()
      if (editableEvent) {
        this.updateEventPosition(editableEvent, newStartTime)
      } else {
        this.createNewEvent(newStartTime, newEndTime)
      }

      this.updateCalendarWithReset()
    }

    /**
     * Crea un nuevo evento
     */
    createNewEvent (startTime: number, endTime: number): void {
      const newEvent = {
        id: `event-${Date.now()}`,
        name: this.getTaskName(),
        color: this.getTaskColor(),
        start: startTime,
        end: endTime,
        timed: true,
        hasConflict: false,
        originalName: this.getTaskName(),
        originalColor: this.getTaskColor(),
        icon: this.getTaskIcon(),
      }
      this.events = [...this.events, newEvent]
    }

    /**
     * Valida si una hora está dentro del rango permitido y no ha pasado (si es hoy)
     */
    isTimeInAllowedRange (tms: any): boolean {
      if (!this.startHour || !this.endHour) return true

      const clickHour = tms.hour
      const clickMinute = tms.minute

      // Parsing de horas permitidas
      const startParts = this.startHour.split(':')
      const startHours = parseInt(startParts[0]) || 8
      const startMinutes = parseInt(startParts[1]) || 0

      const endParts = this.endHour.split(':')
      const endHours = parseInt(endParts[0]) || 19
      const endMinutes = parseInt(endParts[1]) || 0

      // Convertir todo a minutos para comparación fácil
      const clickTotalMinutes = clickHour * 60 + clickMinute
      const startTotalMinutes = startHours * 60 + startMinutes
      const endTotalMinutes = endHours * 60 + endMinutes

      // Verificar que esté dentro del rango de horario permitido
      const isInTimeRange = clickTotalMinutes >= startTotalMinutes && clickTotalMinutes <= endTotalMinutes

      // Si no está en el rango, retornar false
      if (!isInTimeRange) {
        return false
      }

      // Verificar si el slot de tiempo ya pasó (solo para el día actual)
      const slotTimestamp = this.toTime(tms)
      return this.isTimeRangeAvailable(slotTimestamp, slotTimestamp + this.taskDurationMs)
    }

    /**
     * Extiende un evento desde la parte inferior
     */
    extendBottom (event: CalendarEvent): void {
      if (!event.readonly) {
        // Preservar información original si no existe
        if (!event.originalName) {
          event.originalName = event.name
        }
        if (!event.originalColor) {
          event.originalColor = event.color
        }

        this.createEvent = event
        this.createStart = event.start as number
        this.extendOriginal = event.end as number
      }
    }

    /**
     * Maneja el movimiento del mouse para arrastrar o redimensionar eventos
     */
    mouseMove (tms: any): void {
      const mouse = this.toTime(tms)

      if (this.dragEvent && this.dragTime !== null) {
        const newStartTime = mouse - this.dragTime
        const newStart = this.roundTime(newStartTime)
        const newEnd = newStart + this.taskDurationMs // Usar siempre la duración configurada

        // Actualizar el evento de manera reactiva
        this.updateEventPosition(this.dragEvent, newStart)

        // Actualizar la selección temporal durante el arrastre
        this.temporarySelection = `${this.formatEventTime(newStart)} - ${this.formatEventTime(newEnd)}`

        // Validar conflictos durante el arrastre de eventos existentes
        this.$nextTick(() => {
          this.validateAllEvents()
          if (this.$refs.calendar) {
            (this.$refs.calendar as any).checkChange()
          }
        })
      } else if (this.createEvent && this.createStart !== null) {
        const mouseRounded = this.roundTime(mouse, false)

        // Para eventos nuevos, usar la duración configurada
        if (mouseRounded > this.createStart) {
          this.createEvent.start = this.createStart
          this.createEvent.end = this.createStart + this.taskDurationMs
        } else {
          this.createEvent.start = mouseRounded
          this.createEvent.end = mouseRounded + this.taskDurationMs
        }

        // Actualizar la selección temporal durante la creación
        this.temporarySelection = `${this.formatEventTime(this.createEvent.start)} - ${this.formatEventTime(this.createEvent.end)}`

        // Validar conflictos durante la creación/movimiento
        this.validateAndUpdateConflict()
      }
    }

    /**
     * Convierte un valor de tiempo a número (timestamp) de manera segura
     */
    ensureTimeAsNumber (time: string | number): number {
      if (typeof time === 'number') {
        return time
      }
      // Si es string, intentar parsearlo como timestamp o fecha
      const parsed = typeof time === 'string' ? new Date(time).getTime() : time
      return isNaN(parsed) ? Date.now() : parsed
    }

        /**
     * Ajusta los tiempos de un evento dentro del rango permitido manteniendo la duración exacta de la tarea
     */
    adjustEventTimesToRange (start: number, end: number, minTime: number, maxTime: number): { newStart: number; newEnd: number } {
      const taskDuration = this.taskDurationMs
      let newStart = start
      let newEnd = end

            // Si el evento se pasa del rango por debajo (inicio antes de minTime)
      if (start < minTime) {
        newStart = minTime
        newEnd = newStart + taskDuration
      } else if (end > maxTime) {
        // Si el evento se pasa del rango por arriba (fin después de maxTime)
        newEnd = maxTime
        newStart = newEnd - taskDuration
      } else if ((end - start) !== taskDuration) {
        // Si está dentro del rango pero no tiene la duración correcta, ajustar
        newEnd = newStart + taskDuration
        // Verificar si el ajuste hace que se salga del rango
        if (newEnd > maxTime) {
          newEnd = maxTime
          newStart = newEnd - taskDuration
        }
      }

      // Validación final: asegurar que el evento esté completamente dentro del rango
      if (newStart < minTime) {
        newStart = minTime
        newEnd = newStart + taskDuration
      }
      if (newEnd > maxTime) {
        newEnd = maxTime
        newStart = newEnd - taskDuration
      }

      return { newStart, newEnd }
    }

    /**
     * Valida que un evento tenga tiempos válidos y la duración correcta
     */
    validateEventTimes (start: number, end: number, minTime: number, maxTime: number): { newStart: number; newEnd: number } {
      let newStart = start
      let newEnd = end

      // Asegurar que el evento tenga la duración exacta de la tarea
      const expectedDuration = this.taskDurationMs
      const actualDuration = newEnd - newStart

      if (actualDuration !== expectedDuration || newStart >= newEnd) {
        // Reestablecer la duración correcta
        newEnd = newStart + expectedDuration

        // Si se sale del rango, ajustar manteniendo la duración
        if (newEnd > maxTime) {
          newEnd = maxTime
          newStart = newEnd - expectedDuration
        }
        if (newStart < minTime) {
          newStart = minTime
          newEnd = newStart + expectedDuration
        }
      }

      return { newStart, newEnd }
    }

    /**
     * Procesa el arrastre de un evento existente
     */
    processDragEvent (): void {
      if (!this.dragEvent) return

      const dragStart = this.ensureTimeAsNumber(this.dragEvent.start)
      const dragEnd = this.ensureTimeAsNumber(this.dragEvent.end)

      // Extraer la fecha del evento que se está arrastrando
      const eventDate = dayjs(dragStart).format('YYYY-MM-DD')
      const { minTime, maxTime } = this.calculateTimeRange(eventDate)

      const { newStart, newEnd } = this.adjustEventTimesToRange(dragStart, dragEnd, minTime, maxTime)

      // Aplicar los cambios al evento
      this.dragEvent.start = newStart
      this.dragEvent.end = newEnd

      // Actualizar la selección temporal para reflejar los cambios
      this.temporarySelection = `${this.formatEventTime(newStart)} - ${this.formatEventTime(newEnd)}`
    }

    /**
     * Procesa la creación de un nuevo evento
     */
    processCreateEvent (): void {
      if (!this.createEvent) return

      const createStart = this.ensureTimeAsNumber(this.createEvent.start)
      const createEnd = this.ensureTimeAsNumber(this.createEvent.end)

      // Extraer la fecha del evento que se está creando
      const eventDate = dayjs(createStart).format('YYYY-MM-DD')
      const { minTime, maxTime } = this.calculateTimeRange(eventDate)

      // Ajustar el evento para mantener la duración exacta de la tarea
      const { newStart, newEnd } = this.adjustEventTimesToRange(createStart, createEnd, minTime, maxTime)

      this.createEvent.start = newStart
      this.createEvent.end = newEnd

      // Actualizar la selección temporal con los valores corregidos
      this.temporarySelection = `${this.formatEventTime(this.createEvent.start)} - ${this.formatEventTime(this.createEvent.end)}`
    }

    /**
     * Finaliza el arrastre o creación de eventos
     */
    endDrag (): void {
      // Procesar eventos de arrastre y creación
      this.processDragEvent()
      this.processCreateEvent()

      // Limpiar variables de arrastre
      this.dragTime = null
      this.dragEvent = null
      this.createEvent = null
      this.createStart = null
      this.extendOriginal = null

      // Validar conflictos después de cualquier operación de arrastre
      this.$nextTick(() => {
        this.validateAllEvents()
        if (this.$refs.calendar) {
          (this.$refs.calendar as any).checkChange()
        }
        this.$forceUpdate()
      })
    }

    /**
     * Cancela la operación de arrastre
     */
    cancelDrag (): void {
      if (this.createEvent) {
        if (this.extendOriginal) {
          this.createEvent.end = this.extendOriginal
        } else {
          const i = this.events.indexOf(this.createEvent)
          if (i !== -1) {
            this.events.splice(i, 1)
          }
        }
      }

      // Restaurar estado después de cancelar
      this.createEvent = null
      this.createStart = null
      this.dragTime = null
      this.dragEvent = null

      // Validar conflictos después de cancelar para limpiar estados
      this.$nextTick(() => {
        this.validateAllEvents()
      })
    }

    /**
     * Redondea el tiempo al intervalo configurado
     */
    roundTime (time: number, down = true): number {
      const roundTo = this.intervalMinutes // minutos dinámicos
      const roundDownTime = roundTo * 60 * 1000

      return down
        ? time - time % roundDownTime
        : time + (roundDownTime - (time % roundDownTime))
    }

    /**
     * Parsea el valor de fecha en diferentes formatos
     */
        parseBaseDate (value: string): Date {
      if (!value) {
        return new Date()
      }

      // Si el value está en formato dd/mm/yyyy, convertirlo
      if (value.includes('/')) {
        const parts = value.split('/')
        if (parts.length === 3) {
          // Formato dd/mm/yyyy -> convertir a yyyy-mm-dd
          const day = parts[0].padStart(2, '0')
          const month = parts[1].padStart(2, '0')
          const year = parts[2]
          const isoFormat = `${year}-${month}-${day}`

          return new Date(isoFormat)
        }
      }

      // Asumir que está en formato ISO (YYYY-MM-DD)
      return new Date(value)
    }

    /**
     * Convierte el objeto de tiempo del calendario a timestamp
     */
    toTime (tms: any): number {
      try {
        // Usar selectedDate como fuente de verdad
        let targetDateISO: string

        if (this.selectedDate) {
          // Convertir selectedDate (DD/MM/YYYY) a formato ISO (YYYY-MM-DD)
          if (this.selectedDate.includes('/')) {
            const parts = this.selectedDate.split('/')
            targetDateISO = `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`
          } else {
            targetDateISO = this.selectedDate
          }
        } else {
          targetDateISO = this.value || dayjs().format('YYYY-MM-DD')
        }

        // Crear fecha usando componentes individuales para evitar problemas de zona horaria
        const dateParts = targetDateISO.split('-')
        const year = parseInt(dateParts[0])
        const month = parseInt(dateParts[1]) - 1 // Los meses en JS van de 0-11
        const day = parseInt(dateParts[2])

        return new Date(year, month, day, tms.hour, tms.minute).getTime()
      } catch (error) {
        return this.createFallbackTimestamp(tms)
      }
    }

    /**
     * Crea un timestamp de fallback usando la fecha correcta
     */
    createFallbackTimestamp (tms: any): number {
      // Usar selectedDate como fuente de verdad
      let targetDateISO: string

      if (this.selectedDate && this.selectedDate.includes('/')) {
        // Convertir DD/MM/YYYY a YYYY-MM-DD
        const parts = this.selectedDate.split('/')
        targetDateISO = `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`
      } else {
        targetDateISO = this.selectedDate || this.value || dayjs().format('YYYY-MM-DD')
      }

      const dateParts = targetDateISO.split('-')
      const year = parseInt(dateParts[0])
      const month = parseInt(dateParts[1]) - 1 // Los meses en JS van de 0-11
      const day = parseInt(dateParts[2])

      return new Date(year, month, day, tms.hour, tms.minute).getTime()
    }

    /**
     * Obtiene el color del evento según su estado
     */
    getEventColor (event: CalendarEvent): string {
      const rgb = parseInt(event.color.substring(1), 16)
      const r = (rgb >> 16) & 0xFF
      const g = (rgb >> 8) & 0xFF
      const b = (rgb >> 0) & 0xFF

      return event === this.dragEvent
        ? `rgba(${r}, ${g}, ${b}, 0.7)`
        : event === this.createEvent
          ? `rgba(${r}, ${g}, ${b}, 0.7)`
          : event.color
    }

            /**
     * Genera eventos para el rango de fechas seleccionado
     */
    getEvents ({ start, end }: { start: any; end: any }): void {
      const events: CalendarEvent[] = []

      if (!this.startHour || !this.endHour) {
        this.events = events
        return
      }

      // Usar selectedDate como fuente de verdad, convertir al formato correcto
      const targetDate = this.selectedDate ? this.formatDateToISO(this.selectedDate) : start.date
      const { minTime, maxTime } = this.calculateTimeRange(targetDate)

      // Agregar evento de ejemplo (solo si no está en slot pasado)
      const sampleEvent = this.generateSingleEvent(1, minTime, maxTime)
      if (sampleEvent && this.isTimeRangeAvailable(sampleEvent.start as number, sampleEvent.end as number)) {
        events.push(sampleEvent)
      }

      // Agregar agendas existentes de la fecha correspondiente
      const agendaEvents = this.filterAgendasByDate(targetDate)
      events.push(...agendaEvents)
      this.events = events

      // Validar conflictos después de cargar los eventos
      this.$nextTick(() => {
        this.validateAllEvents()
        // Siempre aplicar estilos de intervalos (limpiará estilos para días futuros)
        this.applyPastIntervalStyles()
      })
    }

    /**
     * Filtra las agendas por fecha y las convierte a eventos del calendario
     */
    filterAgendasByDate (calendarDate: string): CalendarEvent[] {
      const agendaEvents: CalendarEvent[] = []

      for (const agenda of this.agendas) {
        if (agenda.timeStampStart && agenda.timeStampEnd) {
          const agendaDate = dayjs(agenda.timeStampStart).format('YYYY-MM-DD')
          if (agendaDate === calendarDate) {
            const event: CalendarEvent = {
              id: agenda?.id?.toString() || `agenda-${Date.now()}`,
              name: agenda.type.description,
              color: agenda.type.color,
              start: agenda.timeStampStart,
              startTime: agenda.timeStart,
              endTime: agenda.timeEnd,
              end: agenda.timeStampEnd,
              timed: true,
              details: `Agenda ocupada: ${agenda.type.description}`,
              readonly: true, // Los eventos de agendas son de solo lectura
              hasConflict: false,
              originalName: agenda.type.description,
              originalColor: agenda.type.color,
              icon: agenda.type.icon?.toString() || '', // Convertir a string para evitar error de tipos
            }
            agendaEvents.push(event)
          }
        }
      }

      return agendaEvents
    }

    /**
     * Calcula el rango de tiempo mínimo y máximo
     */
    calculateTimeRange (date: string): { minTime: number; maxTime: number } {
      const baseDate = new Date(`${date}T00:00:00`)

      const startParts = this.startHour.split(':')
      const startHours = parseInt(startParts[0]) || 8
      const startMinutes = parseInt(startParts[1]) || 0

      const endParts = this.endHour.split(':')
      const endHours = parseInt(endParts[0]) || 19
      const endMinutes = parseInt(endParts[1]) || 0

      const minTime = new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate(), startHours, startMinutes).getTime()
      const maxTime = new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate(), endHours, endMinutes).getTime()

      return { minTime, maxTime }
    }

    /**
     * Genera un solo evento dentro del rango permitido
     */
    generateSingleEvent (index: number, minTime: number, maxTime: number): CalendarEvent | null {
      // Usar exactamente la duración especificada en el prop
      const eventDuration = this.taskDurationMs

      const availableTime = maxTime - minTime - eventDuration
      if (availableTime <= 0) return null

      const randomOffset = this.rnd(0, availableTime)
      const eventStart = minTime + randomOffset
      const roundedStart = this.roundTime(eventStart)

      // El evento debe tener exactamente la duración especificada
      const eventEnd = roundedStart + eventDuration

      if (roundedStart >= minTime && eventEnd <= maxTime) {
        return {
          id: `event-${index}-${Date.now()}`,
          name: this.getTaskName(),
          color: this.getTaskColor(),
          start: roundedStart,
          end: eventEnd,
          timed: true,
          hasConflict: false,
          originalName: this.getTaskName(),
          originalColor: this.getTaskColor(),
          icon: this.getTaskIcon(),
        }
      }

      return null
    }

    /**
     * Genera un número aleatorio entre a y b
     */
    rnd (a: number, b: number): number {
      return Math.floor((b - a + 1) * Math.random()) + a
    }

    /**
     * Selecciona un elemento aleatorio del array
     */
    rndElement (arr: any[]): any {
      return arr[this.rnd(0, arr.length - 1)]
    }

    /**
     * Formatea el tiempo de un evento
     */
    formatEventTime (time: string | number): string {
      if (typeof time === 'number') {
        // Convertir timestamp a fecha y formatear correctamente
        const date = new Date(time)
        return dayjs(date).format('h:mm A')
      } else {
        return dayjs(time).format('h:mm A')
      }
    }

    /**
     * Selecciona un evento y lo pasa al dialog
     */
    selectEvent (event: CalendarEvent, eventElement: Event): void {
      // No permitir selección de eventos deshabilitados o readonly
      if (!event.readonly) {
        this.selectedEvent = { ...event }
        this.selectedOpen = true
        this.selectedElement = eventElement
      }
    }

    /**
     * Obtiene la hora actual en zona horaria chilena
     */
    getCurrentChileanTime (): dayjs.Dayjs {
      return dayjs().tz('America/Santiago')
    }

    /**
     * Verifica si la fecha seleccionada es el día actual en zona horaria chilena
     */
    isSelectedDateToday (): boolean {
      if (!this.selectedDate) return false

      const today = this.getCurrentChileanTime().format('YYYY-MM-DD')
      const selectedDateISO = this.formatDateToISO(this.selectedDate)

      return selectedDateISO === today
    }

    /**
     * Verifica si un slot de tiempo específico ya ha pasado (solo para el día actual)
     */
    isTimeSlotPassed (slotTime: number): boolean {
      // Solo validar si es el día actual
      if (!this.isSelectedDateToday()) {
        return false
      }

      const currentChileanTime = this.getCurrentChileanTime()
      const slotDateTime = dayjs(slotTime).tz('America/Santiago')

      // Comparar con la hora actual más un pequeño buffer de seguridad (5 minutos)
      return slotDateTime.isBefore(currentChileanTime.add(5, 'minute'))
    }

    /**
     * Verifica si un rango de tiempo está disponible para selección
     */
    isTimeRangeAvailable (startTime: number, endTime: number): boolean {
      // Si no es hoy, todos los horarios están disponibles
      if (!this.isSelectedDateToday()) {
        return true
      }

      // Si es hoy, verificar que el slot no haya pasado
      return !this.isTimeSlotPassed(startTime)
    }

    /**
     * Watcher para selectedDate - aplica estilos correctos cuando cambia la fecha
     */
    @Watch('selectedDate')
    onSelectedDateChanged (): void {
      // Cuando cambie la fecha seleccionada, aplicar estilos correctos
      if (this.isDialogOpen) {
        this.$nextTick(() => {
          this.applyPastIntervalStyles()
        })
      }
    }

    @Watch('currentSelectedTime')
    onCurrentSelectedTimeChanged (): void {
      if (this.currentSelectedTime?.length) {
        this.selectedTime = this.currentSelectedTime
      }
    }

    /**
     * Inicia el proceso de mostrar detalles al hacer hover sobre un evento
     */
  startHoverDetail (event: CalendarEvent, mouseEvent: MouseEvent | TouchEvent): void {
      // Solo mostrar detalles para eventos que tienen información de agenda (readonly)
      if (!event.readonly || !event.id) return

      // Cancelar solo el timer anterior, sin ejecutar la lógica de cierre
      if (this.hoverTimer) {
        clearTimeout(this.hoverTimer)
        this.hoverTimer = null
      }

      // Guardar el evento actual
      this.currentHoveredEvent = event

      // Calcular posición del menu
      this.calculateMenuPosition(mouseEvent)

      // En móvil, mostrar inmediatamente. En desktop, usar delay
      const delay = this.isMobile ? 0 : 300

      // Iniciar timer para mostrar el detalle
    this.hoverTimer = setTimeout(() => {
        this.showEventDetail(event)
      }, delay)
    }

        /**
     * Calcula la posición del menú basado en el evento y el dispositivo
     */
    calculateMenuPosition (event: MouseEvent | TouchEvent): void {
      const target = event.target as HTMLElement
      const rect = target.getBoundingClientRect()

      // Dimensiones del menú según el dispositivo
      const menuHeight = 200
      const menuWidth = this.isMobile ? 280 : 300

      // Posición horizontal: centrado sobre el evento
      this.positionX = rect.left + (rect.width / 2) - (menuWidth / 2)

      // Posición vertical: arriba del evento
      this.positionY = rect.top - menuHeight - 10

      // Ajustar si se sale de la pantalla
      const viewportWidth = window.innerWidth
      const margin = this.isMobile ? 20 : 10 // Más margen en móvil

      // Ajustar horizontalmente si se sale de la pantalla
      if (this.positionX < margin) {
        this.positionX = margin
      } else if (this.positionX + menuWidth > viewportWidth - margin) {
        this.positionX = viewportWidth - menuWidth - margin
      }

      // Si no hay espacio arriba, mostrar abajo
      if (this.positionY < margin) {
        this.positionY = rect.bottom + 10
      }
    }

    /**
     * Maneja el evento touch start en dispositivos móviles
     */
        handleTouchStart (event: CalendarEvent, touchEvent: TouchEvent): void {
      if (!this.isMobile) return

      // Prevenir el comportamiento por defecto para evitar scroll
      touchEvent.preventDefault()

      // Llamar al método principal de hover
      this.startHoverDetail(event, touchEvent)
    }

    /**
     * Maneja el evento touch end en dispositivos móviles
     */
    handleTouchEnd (): void {
      if (!this.isMobile) return

      // En móvil, cerrar el detalle después de un tiempo
      setTimeout(() => {
        this.closeEventDetail()
      }, 3000) // Mostrar por 3 segundos en móvil
    }

    /**
     * Cancela el hover y oculta el detalle si está visible
     */
    cancelHoverDetail (): void {
      // Cancelar timer si existe
      if (this.hoverTimer) {
        clearTimeout(this.hoverTimer)
        this.hoverTimer = null
      }

      // Ocultar menu después de un pequeño delay para permitir mover el mouse al menu
      setTimeout(() => {
        if (!this.isMouseOverMenu()) {
          this.closeEventDetail()
        }
      }, 200) // Aumentamos el delay a 200ms para dar más tiempo
    }

    /**
     * Verifica si el mouse está sobre el menu de detalles
     */
    isMouseOverMenu (): boolean {
      // Esta función se puede mejorar con detección más precisa si es necesario
      return this.showDetailMenu && this.leadDetail.lead !== null
    }

    /**
     * Muestra el detalle del evento cargando los datos del lead
     */
    async showEventDetail (event: CalendarEvent): Promise<void> {
      if (!event.id || !event.readonly) return
      try {
        // Mostrar loading
        this.isLoadingDetail = true
        this.showDetailMenu = true

        // Buscar la agenda correspondiente al evento
        const agenda = this.agendas.find(a => a.id?.toString() === event.id)
        if (!agenda || !agenda.lead?.id) {
          this.closeEventDetail()
          return
        }

        // Cargar datos del lead
        const leadData = await this.fetchData({
          query: { name: 'fetch', model: 'Lead', params: { id: agenda.lead.id } },
          force: true,
        })

        // Configurar los datos para el componente TaskDetail
        this.leadDetail = {
          lead: leadData,
          task: agenda,
        }

        this.isLoadingDetail = false
      } catch (error) {
        console.error('Error cargando detalles del evento:', error)
        this.closeEventDetail()
      }
    }

    /**
     * Cierra el menu de detalles del evento
     */
    closeEventDetail (): void {
      this.showDetailMenu = false
      this.isLoadingDetail = false
      this.leadDetail = { lead: null, task: null }
      this.currentHoveredEvent = null

      // Limpiar timer si existe
      if (this.hoverTimer) {
        clearTimeout(this.hoverTimer)
        this.hoverTimer = null
      }
    }

    /**
     * Maneja cuando el mouse entra al menu de detalles
     */
    onMenuMouseEnter (): void {
      // Cancelar cualquier timer de cierre cuando el mouse está sobre el menu
      if (this.hoverTimer) {
        clearTimeout(this.hoverTimer)
        this.hoverTimer = null
      }
    }

    /**
     * Maneja cuando el mouse sale del menu de detalles
     */
    onMenuMouseLeave (): void {
      // Cerrar el menu cuando el mouse sale
      this.closeEventDetail()
    }

    /**
     * Obtiene el nombre de la tarea desde newAgenda o valor por defecto
     */
    getTaskName (): string {
      return this.newAgenda?.title?.description || 'Inspección'
    }

    /**
     * Obtiene el color de la tarea desde newAgenda o valor por defecto
     */
    getTaskColor (): string {
      return this.newAgenda?.title?.color || this.rndElement(this.colors)
    }

    /**
     * Obtiene el icono de la tarea desde newAgenda
     */
    getTaskIcon (): string | undefined {
      return this.newAgenda?.title?.icon?.toString()
    }
  }
